import {
  ApiResponse,
  SendNotificationRequest,
  SendNotificationWithCountResponse,
  UserPushTokensResponse,
  DeletePushTokenResponse,
  NotificationHistoryResponse,
  NotificationType,
  DeliveryStats,
  NotificationLog,
  NotificationEvent,
  QueueStatusResponse,
  NotificationChannel,
  SeedNotificationTypesResponse,
} from '@mygate/common';
import { api } from './api';

// Response types for the new notification system endpoints
export interface NotificationTypesResponse {
  types: NotificationType[];
}

export interface NotificationTypeResponse {
  type: NotificationType;
}

export interface NotificationAnalyticsResponse {
  stats: DeliveryStats;
}

export interface NotificationLogsResponse {
  logs: Partial<NotificationLog>[];
  total: number;
  page: number;
  limit: number;
}

export interface NotificationEventsResponse {
  events: Partial<NotificationEvent>[];
  total: number;
  page: number;
  limit: number;
}

export interface TriggerNotificationResponse {
  eventId: string;
  message: string;
}

export interface LabTriggerResponse {
  eventId: string;
  eventType: string;
  userId: string;
}

export interface UpdateNotificationTypeRequest {
  templates?: Record<string, {
    title: string;
    body: string;
    deepLink?: string;
    whatsappTemplateId?: string;
  }>;
  channels?: {
    primary: NotificationChannel;
    fallbackChain: NotificationChannel[];
  };
  repeatConfig?: {
    intervals: number[];
    maxPerPeriod?: number;
    periodDays?: number;
  };
  isEnabled?: boolean;
}

class NotificationsService {
  /**
   * Send push notification to users
   */
  async sendNotification(request: SendNotificationRequest): Promise<ApiResponse<SendNotificationWithCountResponse>> {
    return api.post('/api/staff/notifications/send', request);
  }

  /**
   * Get push tokens for a specific user
   */
  async getUserPushTokens(userId: string): Promise<ApiResponse<UserPushTokensResponse>> {
    return api.get(`/api/staff/users/${userId}/push-tokens`);
  }

  /**
   * Get the pushes sent to one customer from the backoffice, as broadcasts or as card renewal notices (kept 30
   * days). Pushes of the event-driven notification system are in the logs (`getLogs`), 3DS pushes nowhere.
   */
  async getUserPushHistory(
    userId: string,
    params: { page: number; limit: number },
  ): Promise<ApiResponse<NotificationHistoryResponse>> {
    const query = new URLSearchParams({ userId, page: String(params.page), limit: String(params.limit) });
    return api.get(`/api/staff/notifications/history?${query.toString()}`);
  }

  /**
   * Delete a push token
   */
  async deletePushToken(tokenId: string): Promise<ApiResponse<DeletePushTokenResponse>> {
    return api.delete(`/api/staff/push-tokens/${tokenId}`);
  }

  // ============================
  // Notification System Management
  // ============================

  /**
   * Get all notification types
   */
  async getNotificationTypes(): Promise<ApiResponse<NotificationTypesResponse>> {
    return api.get('/api/staff/notifications/types');
  }

  /**
   * Get a specific notification type by code
   */
  async getNotificationTypeByCode(code: string): Promise<ApiResponse<NotificationTypeResponse>> {
    return api.get(`/api/staff/notifications/types/${code}`);
  }

  /**
   * Update a notification type
   */
  async updateNotificationType(
    code: string,
    data: UpdateNotificationTypeRequest,
  ): Promise<ApiResponse<NotificationTypeResponse>> {
    return api.put(`/api/staff/notifications/types/${code}`, data);
  }

  /**
   * Toggle notification type enabled status
   */
  async toggleNotificationType(code: string, enabled: boolean): Promise<ApiResponse<NotificationTypeResponse>> {
    return api.post(`/api/staff/notifications/types/${code}/toggle`, { enabled });
  }

  /**
   * Get notification system analytics
   */
  async getAnalytics(params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<NotificationAnalyticsResponse>> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.set('startDate', params.startDate);
    if (params?.endDate) queryParams.set('endDate', params.endDate);
    const query = queryParams.toString();
    return api.get(`/api/staff/notifications/system-analytics${query ? `?${query}` : ''}`);
  }

  /**
   * Get notification logs
   */
  async getLogs(params?: {
    userId?: string;
    typeCode?: string;
    status?: string;
    eventId?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<NotificationLogsResponse>> {
    const queryParams = new URLSearchParams();
    if (params?.userId) queryParams.set('userId', params.userId);
    if (params?.typeCode) queryParams.set('typeCode', params.typeCode);
    if (params?.status) queryParams.set('status', params.status);
    if (params?.eventId) queryParams.set('eventId', params.eventId);
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    const query = queryParams.toString();
    return api.get(`/api/staff/notifications/logs${query ? `?${query}` : ''}`);
  }

  /**
   * Get notification events (queue)
   */
  async getEvents(params?: {
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<NotificationEventsResponse>> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.set('status', params.status);
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    const query = queryParams.toString();
    return api.get(`/api/staff/notifications/events${query ? `?${query}` : ''}`);
  }

  /**
   * Get notification queue status
   */
  async getQueueStatus(): Promise<ApiResponse<QueueStatusResponse>> {
    return api.get('/api/staff/notifications/queue-status');
  }

  /**
   * Trigger a test notification
   */
  async triggerTestNotification(
    userId: string,
    typeCode: string,
  ): Promise<ApiResponse<TriggerNotificationResponse>> {
    return api.post('/api/staff/notifications/trigger', { userId, typeCode });
  }

  /**
   * Laboratory: trigger notification bypassing isEnabled check
   */
  async labTrigger(params: {
    userId: string;
    eventType: string;
    placeholders?: Record<string, string>;
    channelOverride?: string;
  }): Promise<ApiResponse<LabTriggerResponse>> {
    return api.post('/api/staff/notifications/lab/trigger', params) as Promise<ApiResponse<LabTriggerResponse>>;
  }

  /**
   * Seed / reset notification types to default definitions
   */
  async seedNotificationTypes(): Promise<ApiResponse<SeedNotificationTypesResponse>> {
    return api.post('/api/staff/notifications/seed', {});
  }
}

export const notificationsService = new NotificationsService();
